import React from "react";
import { Autocomplete, CircularProgress, InputAdornment } from "@mui/material";
import BootstrapTextField from "./BootstrapTextField";
import CustomStyle, { autocompleteDropdownStyles } from "Constants/Style";

interface WarehouseOption {
  warehouse_id: string | number;
  warehouse_name: string;
  warehouse_type?: string;
  [key: string]: any;
}

interface WarehouseAutocompleteProps {
  // Required props
  options: WarehouseOption[];
  value: string | number | null | undefined; // warehouse_id or warehouse_name depending on valueType
  onChange: (warehouseId: string | number, warehouseName: string, fullOption?: WarehouseOption) => void;

  // Optional props
  loading?: boolean;
  showWarehouseType?: boolean; // Show warehouse type in label (e.g., "Name (Type)")
  valueType?: "warehouse_id" | "warehouse_name"; // What type of value is being used
  disableClearable?: boolean;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  filterOptions?: (option: WarehouseOption) => boolean; // Custom filter function
  sessionStorageKey?: string; // Key to save selected warehouse in sessionStorage
  onBlur?: () => void;
  name?: string; // For formik
  formik?: any; // Formik instance (if using formik)
  formikFieldId?: string; // Formik field name for warehouse_id
  formikFieldName?: string; // Formik field name for warehouse_name
  customSx?: any; // Custom Material-UI sx prop
  inputSx?: any; // Custom input sx prop
  size?: "small" | "medium";
  fullWidth?: boolean;
}

const WarehouseAutocomplete: React.FC<WarehouseAutocompleteProps> = ({
  options = [],
  value,
  onChange,
  loading = false,
  showWarehouseType = false,
  valueType = "warehouse_id",
  disableClearable = false,
  placeholder = "Select Warehouse",
  label,
  required = false,
  error = false,
  helperText,
  disabled = false,
  filterOptions,
  sessionStorageKey,
  onBlur,
  name,
  formik,
  formikFieldId,
  formikFieldName,
  customSx,
  inputSx,
  size = "small",
  fullWidth = true,
}) => {
  // Get option label with or without type
  const getOptionLabel = (option: WarehouseOption): string => {
    const name = option?.warehouse_name || "";
    if (showWarehouseType && option?.warehouse_type) {
      return `${name} (${option.warehouse_type})`;
    }
    return name;
  };

  // Find the selected option based on value type
  const getSelectedOption = (): WarehouseOption | null => {
    if (!value) return null;
    return (
      options.find((opt) => {
        if (valueType === "warehouse_id") {
          return opt.warehouse_id === value;
        } else {
          return opt.warehouse_name === value;
        }
      }) || null
    );
  };

  // Filter options if filterOptions function is provided
  const filteredOptions = filterOptions
    ? options.filter(filterOptions)
    : options;


  // Handle change
  const handleChange = (
    event: React.SyntheticEvent,
    selectedOption: WarehouseOption | null
  ) => {
    if (selectedOption) {
      const warehouseId = selectedOption.warehouse_id;
      const warehouseName = selectedOption.warehouse_name || "";

      // If formik is provided, update formik fields
      if (formik) {
        if (formikFieldId) {
          formik.setFieldValue(formikFieldId, warehouseId);
        }
        if (formikFieldName) {
          formik.setFieldValue(formikFieldName, warehouseName);
          // Clear error if a valid warehouse is selected
          if (warehouseName) {
            formik.setFieldError(formikFieldName, undefined);
          }
        }
        // Clear error for the name field if provided
        if (name && warehouseName) {
          formik.setFieldError(name, undefined);
        }
      }

      // Save to sessionStorage if key is provided
      if (sessionStorageKey) {
        sessionStorage.setItem(sessionStorageKey, String(warehouseId));
      }

      // Call the onChange callback
      onChange(warehouseId, warehouseName, selectedOption);
    } else if (!disableClearable) {
      // Handle clear (only if clearable is enabled)
      if (formik) {
        if (formikFieldId) {
          formik.setFieldValue(formikFieldId, "");
        }
        if (formikFieldName) {
          formik.setFieldValue(formikFieldName, "");
        }
      }
      onChange("", "", undefined);
    }
  };

  // Get error state (from prop or formik)
  const getError = (): boolean => {
    if (error) return true;
    if (formik && name) {
      return formik.touched[name] && Boolean(formik.errors[name]);
    }
    if (formik && formikFieldName) {
      return (
        formik.touched[formikFieldName] && Boolean(formik.errors[formikFieldName])
      );
    }
    if (formik && formikFieldId) {
      return (
        formik.touched[formikFieldId] && Boolean(formik.errors[formikFieldId])
      );
    }
    return false;
  };

  // Get helper text (from prop or formik)
  const getHelperText = (): string | undefined => {
    if (helperText) return helperText;
    if (formik && name) {
      return formik.touched[name] && formik.errors[name]
        ? formik.errors[name]
        : undefined;
    }
    if (formik && formikFieldName) {
      return formik.touched[formikFieldName] && formik.errors[formikFieldName]
        ? formik.errors[formikFieldName]
        : undefined;
    }
    if (formik && formikFieldId) {
      return formik.touched[formikFieldId] && formik.errors[formikFieldId]
        ? formik.errors[formikFieldId]
        : undefined;
    }
    return undefined;
  };

  // Default sx styles
  const defaultSx = CustomStyle?.singleselect || {};
  const defaultInputSx = autocompleteDropdownStyles.inputSx || {};

  return (
    <Autocomplete
      options={filteredOptions}
      loading={loading}
      getOptionLabel={getOptionLabel}
      value={getSelectedOption()}
      onChange={handleChange}
      onBlur={onBlur}
      disableClearable={disableClearable}
      disabled={disabled}
      sx={{ ...defaultSx, ...customSx }}
      renderOption={(props, option) =>
        autocompleteDropdownStyles.renderOption(
          props,
          option,
          getOptionLabel
        )
      }
      ListboxProps={autocompleteDropdownStyles.ListboxProps}
      renderInput={(params) => (
        <BootstrapTextField
          {...params}
          placeholder={placeholder}
          name={name}
          label={label}
          required={required}
          error={getError()}
          helperText={getHelperText()}
          fullWidth={fullWidth}
          size={size}
          sx={{ ...defaultInputSx, ...inputSx }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} sx={{ color: '#3969E7' }} />
                  </InputAdornment>
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default WarehouseAutocomplete;

